import React, {useEffect, useRef, useState} from 'react';
import {Autocomplete, Box, InputAdornment, Typography} from '@mui/material';
import SectionDetailsHeader from './SectionDetailsHeader';
import styles from '../../../styles/components/clientdetails/projectDrawerStyle.module.scss';
import {t} from 'i18next';
import AurButton from '../../common/aur-button/AurButton';
import {ReactComponent as DropdownIcon} from '../../../assests/svg/down-triangle.svg';
import {ReactComponent as DropdownDisableIcon} from '../../../assests/svg/down-triangle-disable.svg';
import {AurTextField} from '../../common/aur-text-field/AurTextField';
import {IDepartmentDropdown} from '../../../types/project-type/projectDetailsType';
import {getDepartmentsByBranchId} from '../../../api/ClientManagementService';
import {PorjectSectionBaseProp} from './ProjectDrawer';
import {useModal} from '../../../contexts/modalContext/ModalContext';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
export type BusinessStructureProps = {
  branchId: string;
  branchName: string;
  departmentId: string;
  departmentName: string;
} & PorjectSectionBaseProp;

function BusinessStructure({
  branchId,
  branchName,
  departmentId,
  departmentName,
  isEdit = false,
  onEditClickHandler,
  onCancelClickHandler,
  onSaveChangeHandler,
}: BusinessStructureProps) {
  const [departmentList, setDepartmentList] = useState<IDepartmentDropdown[]>(
    []
  );
  const {showModal} = useModal();
  const [selectedDepartment, setSelectedDepartment] =
    useState<IDepartmentDropdown>({
      clientDepartmentId: departmentId,
      name: departmentName,
    });
  const [filedFocusedType, setFiledFocusedType] = useState<'tab' | 'mouse'>(
    'tab'
  );
  const isFocusedByMouse = useRef(false);

  const selectChangeHandler = (
    event: React.SyntheticEvent,
    newValue: IDepartmentDropdown
  ) => {
    setSelectedDepartment(newValue);
  };
  const [
    isGetDepartmentListByBranchIdLoading,
    setIsGetDepartmentListByBranchIdLoading,
  ] = useState(false);

  const saveChangeHandler = () => {
    onSaveChangeHandler({
      departmentId: selectedDepartment?.clientDepartmentId,
      departmentName: selectedDepartment?.name,
    });
  };
  const getDepartmentListByBranchId = async (branchId: string) => {
    setIsGetDepartmentListByBranchIdLoading(true);
    try {
      const result = await getDepartmentsByBranchId(branchId);
      const departmentList = result.data.data as IDepartmentDropdown[];
      setDepartmentList([...departmentList]);
    } catch (error) {
      console.error('getDepartmentListByBranchId', error);
    } finally {
      setIsGetDepartmentListByBranchIdLoading(false);
    }
  };
  const editClickHandler = () => {
    onEditClickHandler({businessStructure: true});
  };
  const hasLocalChanges = () => {
    if (selectedDepartment?.clientDepartmentId != departmentId) {
      return true;
    } else {
      return false;
    }
  };
  const cancelClickHandler = () => {
    if (hasLocalChanges()) {
      showModal({
        title: t(
          'clientDetailsPage.projectDrawer.modal.closePanelUnsavedChangePopup.title'
        ),
        description: t(
          'clientDetailsPage.projectDrawer.modal.closePanelUnsavedChangePopup.description'
        ),
        labelOkButton: t(
          'clientDetailsPage.projectDrawer.modal.closePanelUnsavedChangePopup.okButtonLabel'
        ),
        labelCancelButton: t(
          'clientDetailsPage.projectDrawer.modal.closePanelUnsavedChangePopup.cancelButtonLabel'
        ),
        onOk: () => {
          onCancelClickHandler({businessStructure: false});
        },
      });
    } else {
      onCancelClickHandler({businessStructure: false});
    }
  };
  useEffect(() => {
    if (isEdit) {
      setSelectedDepartment({
        clientDepartmentId: departmentId,
        name: departmentName,
      });
      getDepartmentListByBranchId(branchId);
    }
  }, [isEdit]);

  const borderClass =
    filedFocusedType == 'tab' ? 'tabFocusedBorder' : 'mouseFocusedBorder';

  const editMode = (
    <Box
      className={`${styles.projectDrawerBusinessSturctureDetailsEditContainer}`}
    >
      <SectionDetailsHeader
        headerText={t(`clientDetailsPage.projectDrawer.businessStructure`)}
        onCancelHandler={cancelClickHandler}
        onEditHandler={editClickHandler}
        onSaveChangesHandler={saveChangeHandler}
        isEdit={isEdit}
        isEditButtonVisible={true}
        isSaveButtonDisabled={!hasLocalChanges()}
      />

      <Box className={`${styles.disableDropdownContainer}`}>
        <AurTextField
          disabled
          variant="filled"
          label={t('clientDetailsPage.projectDrawer.branch')}
          value={branchName}
          className={`aur-textfield custom-text-field ${styles.dropdown} ${styles.noBorderDisabled} ${styles.inputFieldTypography}`}
          ariaLabel={t('clientDetailsPage.projectDrawer.branch')}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <DropdownDisableIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box className={`${styles.dropdownContainer}`}>
        <Autocomplete
          popupIcon={<DropdownIcon />}
          blurOnSelect
          disableClearable
          options={departmentList}
          getOptionLabel={(option) => option.name ?? ''}
          isOptionEqualToValue={(
            option: IDepartmentDropdown,
            value: IDepartmentDropdown
          ) => option.clientDepartmentId === value.clientDepartmentId}
          value={selectedDepartment}
          onChange={selectChangeHandler}
          onFocus={(_e: any) => {
            if (isFocusedByMouse.current) {
              isFocusedByMouse.current = false;
              return;
            }
            setFiledFocusedType('tab');
          }}
          renderInput={(props) => (
            <AurTextField
              {...props}
              label={t('clientDetailsPage.projectDrawer.department')}
              variant="filled"
              className={`aur-textfield custom-text-field  ${styles.dropdown} ${styles.inputFieldTypography} ${borderClass}`}
              ariaLabel={t('clientDetailsPage.projectDrawer.department')}
              helperText=""
              onMouseDown={(_e: any) => {
                isFocusedByMouse.current = true;
                setFiledFocusedType('mouse');
              }}
            />
          )}
        />
      </Box>
    </Box>
  );

  const viewMode = (
    <>
      <SectionDetailsHeader
        headerText={t(`clientDetailsPage.projectDrawer.businessStructure`)}
        onCancelHandler={cancelClickHandler}
        onEditHandler={editClickHandler}
        onSaveChangesHandler={saveChangeHandler}
        isEdit={isEdit}
        isEditButtonVisible={false}
      />
      <Box className={`${styles.projectBusinessContainer}`}>
        <Box className={`${styles.projectBranchInfo}`}>
          <Typography
            className={`typo-text subhead4_size ${styles.projectDrawerPropertyText}`}
          >
            {t('clientDetailsPage.projectDrawer.branch')}
          </Typography>
          <Typography
            className={`typo-text body2_size ${styles.projectDrawerPropertyValue}`}
          >
            {branchName}
          </Typography>
        </Box>
        <Box className={`${styles.projectBranchInfo}`}>
          <Typography
            className={`typo-text subhead4_size ${styles.projectDrawerPropertyText}`}
          >
            {t('clientDetailsPage.projectDrawer.department')}
          </Typography>
          <Box className={`${styles.editButtonContainer}`}>
            <Box
              className={`typo-text body2_size ${styles.projectDrawerPropertyValue}`}
            >
              {departmentName}
            </Box>
            <AurButton
              label={t('clientDetailsPage.projectDrawer.change')}
              type="text"
              size="Medium"
              classNames={`${styles.editButton}`}
              onClick={() => editClickHandler()}
            />
          </Box>
        </Box>
      </Box>
    </>
  );
  return (
    <Box>
      <Box
        className={`${styles.projectDrawerBusinessSturctureDetailsContainer}`}
      >
        {isEdit ? editMode : viewMode}
      </Box>
      {
        <AurLoadingIndicator
          overlay={true}
          isOpen={isGetDepartmentListByBranchIdLoading}
        />
      }
    </Box>
  );
}

export default BusinessStructure;
